import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import InitiativeManagementListing from '../../components/InitiativeManagement/InitiativeManagementListing';
import InitiativeManagementForm from '../../components/InitiativeManagement/InitiativeManagementForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { downloadExcel } from '../../utils/excelUtils';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const InitiativeManagement = () => {
    const today = new Date().toISOString().split('T')[0];
    const toast = useRef(null);

    const initialForm = {
        UserId: '', Name: '', InitiativeLevel: '', SolutionType: '', HostInstitution: '',
        ApprovalDate: '', CertificateNumber: '', Summary: '', MembersJSON: '[]', AttachmentUrl: ''
    };

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);
    const [members, setMembers] = useState([]);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [editRequestContent, setEditRequestContent] = useState('');
    const [selectedInitiativeForEdit, setSelectedInitiativeForEdit] = useState(null);

    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectContent, setRejectContent] = useState('');
    const [rejectInitiativeId, setRejectInitiativeId] = useState(null);

    const [users, setUsers] = useState([]);
    const [levels, setLevels] = useState([]);
    const [solutionTypes, setSolutionTypes] = useState([]);

    const [filters, setFilters] = useState({
        authorId: null,
        keyword: '',
        status: '',
        fromDate: null,
        toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'Name', direction: 'asc' });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);

    const [exportFilters, setExportFilters] = useState({
        fromDate: null,
        toDate: null,
        initiativeLevel: '',
        solutionType: '',
        userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa');

    const canModify = currentUser?.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser?.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser?.IsDelegatedAdmin === true);

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            const [uRes, lRes, stRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers: authHeaders }),
                fetch(`${baseUrl}/initiativelevel`, { headers: authHeaders }),
                fetch(`${baseUrl}/solutiontype`, { headers: authHeaders })
            ]);
            if (uRes.ok) setUsers(await uRes.json());
            if (lRes.ok) setLevels(await lRes.json());
            if (stRes.ok) setSolutionTypes(await stRes.json());
        } catch (error) { console.error("Lỗi tải danh mục:", error); }
    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement?userId=${currentUser.Id}&isAdmin=${isManager}`, { headers: authHeaders });
            if (res.ok) {
                const result = await res.json();
                setData(result);
            } else if (res.status === 401) {
                localStorage.removeItem('user');
                localStorage.removeItem('accessToken');
                window.location.href = '/login';
            }
        } catch (error) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu!' }); }
        finally { if (!isBackground) setIsLoading(false); }
    }, [currentUser, isManager, authHeaders]);

    useEffect(() => { fetchData(); fetchDropdowns(); }, [fetchData, fetchDropdowns]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const filteredData = useMemo(() => {
        let result = data.filter(item => {
            if (filters.status && item.Status !== filters.status) return false;

            if (item.ApprovalDate) {
                const itemDate = new Date(item.ApprovalDate);
                itemDate.setHours(0, 0, 0, 0);

                if (filters.fromDate) {
                    const filterFrom = new Date(filters.fromDate);
                    filterFrom.setHours(0, 0, 0, 0);
                    if (itemDate < filterFrom) return false;
                }
                if (filters.toDate) {
                    const filterTo = new Date(filters.toDate);
                    filterTo.setHours(0, 0, 0, 0);
                    if (itemDate > filterTo) return false;
                }
            } else if (filters.fromDate || filters.toDate) {
                return false;
            }

            if (filters.authorId) {
                const targetId = filters.authorId.toString();
                let isMatch = item.UserId?.toString() === targetId;

                if (!isMatch) {
                    try {
                        const mList = JSON.parse(item.MembersJSON || '[]');
                        isMatch = mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                    } catch (e) {
                        isMatch = false;
                    }
                }

                if (!isMatch) return false;
            }

            if (filters.keyword) {
                const query = superCleanString(filters.keyword);
                const initiativeName = superCleanString(item.Name);
                const authorName = superCleanString(item.FullName);
                const certificateNumber = superCleanString(item.CertificateNumber);

                if (!initiativeName.includes(query) && !authorName.includes(query) && !certificateNumber.includes(query)) {
                    return false;
                }
            }

            return true;
        });

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'ApprovalDate') {
                    const dateA = a.ApprovalDate ? new Date(a.ApprovalDate).getTime() : 0;
                    const dateB = b.ApprovalDate ? new Date(b.ApprovalDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        return result;

    }, [data, filters, sortConfig]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleStatusChange = async (id, newStatus, note = '') => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản không có quyền phê duyệt!' });
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, status: newStatus, note })
            });
            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Cập nhật trạng thái thành công!' });
                fetchData(true);
            } else {
                const err = await res.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Thao tác thất bại!' });
            }
        } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' }); }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id, status: newStatus, note: '' })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenRequestEdit = (item) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có quyền yêu cầu chỉnh sửa!' });
        setSelectedInitiativeForEdit(item); setEditRequestContent(''); setIsEditRequestModalOpen(true);
    };

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        await handleStatusChange(selectedInitiativeForEdit.Id, 'Yêu cầu chỉnh sửa', editRequestContent);
        setIsEditRequestModalOpen(false);
    };

    const handleRejectClick = (id) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có quyền từ chối!' });
        setRejectInitiativeId(id);
        setRejectContent('');
        setIsRejectModalOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectContent.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do từ chối!' });
            return;
        }
        await handleStatusChange(rejectInitiativeId, 'Từ chối', rejectContent);
        setIsRejectModalOpen(false);
        setRejectContent('');
        setRejectInitiativeId(null);
    };

    const handleEdit = async (item) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có quyền xem!' });
        if (item.Status === 'Đã duyệt') return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Đã duyệt, không thể sửa!' });
        if (item.IsPendingDeletion) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể sửa sáng kiến đang trong trạng thái chờ duyệt xóa!', life: 4000 });

        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { }

        const missingIds = parsedMembers.map(m => m.userId).filter(id => id && !users.find(u => u.Id.toString() === id.toString()));
        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, { headers: authHeaders });
                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prev => { const merged = [...prev]; extraUsers.forEach(eu => { if (!merged.find(u => u.Id === eu.Id)) merged.push(eu); }); return merged; });
                }
            } catch (e) { }
        }
        setEditId(item.Id);
        setFormData(item);
        setMembers(parsedMembers);
        setIsReadOnly(false);
        setIsModalOpen(true);
    };

    const handleViewDetail = async (item) => {
        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { }

        const missingIds = parsedMembers.map(m => m.userId).filter(id => id && !users.find(u => u.Id.toString() === id.toString()));
        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, { headers: authHeaders });
                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prev => { const merged = [...prev]; extraUsers.forEach(eu => { if (!merged.find(u => u.Id === eu.Id)) merged.push(eu); }); return merged; });
                }
            } catch (e) { }
        }
        setEditId(item.Id);
        setFormData(item);
        setMembers(parsedMembers);
        setIsReadOnly(true);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có quyền xem!' });
        if (data.find(d => d.Id === id)?.Status === 'Đã duyệt') return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Đã duyệt, không thể xóa!' });

        confirmDeleteDialog({
            header: 'Xác nhận xóa', message: 'Bạn có chắc xóa sáng kiến này? Dữ liệu bên Giảng viên cũng sẽ bị xóa.',
            accept: async () => {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement?id=${id}`, { method: 'DELETE', headers: authHeaders });
                if (res.ok) { toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Xóa thành công!' }); fetchData(true); }
                else toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại!' });
            }
        });
    };

    const handleRequestDelete = useCallback((id) => {
        if (!canModify) return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem!' });

        confirmDeleteDialog({
            header: 'Yêu cầu Xóa',
            message: 'Vì lý do bảo mật, dữ liệu sẽ không bị xóa ngay. Hệ thống sẽ tạo "Yêu cầu xóa" và cần 1 Admin quản lý khác vào duyệt. Bạn có tiếp tục?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement?id=${id}&action=request`, {
                        method: 'DELETE', headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Đã gửi yêu cầu', detail: 'Vui lòng chờ Admin quản lý duyệt xóa!' });
                        fetchData(true);
                    } else {
                        const result = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message || 'Thao tác thất bại!' });
                    }
                } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }); }
            }
        });
    }, [canModify, authHeaders, fetchData, confirmDeleteDialog]);

    const handleApproveDelete = useCallback((id) => {
        confirmDeleteDialog({
            header: 'CẢNH BÁO: DUYỆT XÓA VĨNH VIỄN',
            message: 'Hành động này sẽ XÓA VĨNH VIỄN sáng kiến này khỏi hệ thống. Bạn có chắc chắn muốn duyệt yêu cầu này?',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement?id=${id}&action=approve`, {
                        method: 'DELETE', headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Đã xóa', detail: 'Dữ liệu đã bị xóa vĩnh viễn.' });
                        fetchData(true);
                    } else {
                        const result = await response.json();
                        toast.current?.show({ severity: 'error', summary: 'Từ chối', detail: result.message || 'Thao tác thất bại!' });
                    }
                } catch (e) { toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }); }
            }
        });
    }, [authHeaders, fetchData, confirmDeleteDialog]);

    const handleCancelDeleteRequest = useCallback((id) => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement?id=${id}&action=cancel`, {
            method: 'DELETE', headers: authHeaders
        }).then(async res => {
            if (res.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã hủy yêu cầu xóa.' });
                fetchData(true);
            } else {
                const result = await res.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: result.message });
            }
        }).catch(() => toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' }));
    }, [authHeaders, fetchData]);

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các sáng kiến đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận tạo yêu cầu xóa hàng loạt',
            message: `Bạn có chắc chắn muốn tạo yêu cầu xóa cho ${ids.length} sáng kiến đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/initiativemanagement?id=${id}&action=request`, {
                            method: 'DELETE',
                            headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã gửi yêu cầu xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi gửi yêu cầu thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const executeExport = (dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const formattedData = dataToExport.map((item, index) => {
            let mainAuthorName = item.FullName || '';
            try {
                const parsedMembers = JSON.parse(item.MembersJSON || '[]');
                const mainAuthor = parsedMembers.find(m => m.isMainAuthor === true);
                if (mainAuthor) {
                    mainAuthorName = mainAuthor.fullName || mainAuthorName;
                }
            } catch (e) { console.error(e); }

            return {
                'STT': index + 1,
                'Tên sáng kiến': item.Name || '',
                'Cấp sáng kiến': item.InitiativeLevelName || item.InitiativeLevel || '',
                'Loại giải pháp': item.SolutionTypeName || item.SolutionType || '',
                'Đơn vị chủ trì': item.HostInstitution || '',
                'Ngày xét duyệt': item.ApprovalDate ? new Date(item.ApprovalDate).toLocaleDateString('vi-VN') : '',
                'Số quyết định/GCN': item.CertificateNumber || '',
                'Tác giả (Đại diện)': mainAuthorName,
                'Trạng thái': item.Status || 'Lưu nháp'
            };
        });

        const summaryRow = {
            'STT': '', 'Tên sáng kiến': 'TỔNG CỘNG:', 'Cấp sáng kiến': `${dataToExport.length} Sáng kiến`,
            'Loại giải pháp': '', 'Đơn vị chủ trì': '', 'Ngày xét duyệt': '',
            'Số quyết định/GCN': '', 'Tác giả (Đại diện)': '', 'Trạng thái': ''
        };

        const colWidths = [
            { wch: 5 }, { wch: 45 }, { wch: 20 }, { wch: 25 }, { wch: 30 },
            { wch: 15 }, { wch: 20 }, { wch: 25 }, { wch: 15 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `QuanLySangKien_${fileSuffix}`,
            sheetName: 'QuanLySangKien',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    };

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = () => {
        let result = [...data];

        if (exportFilters.fromDate) {
            const filterFrom = new Date(exportFilters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => item.ApprovalDate && new Date(item.ApprovalDate) >= filterFrom);
        }
        if (exportFilters.toDate) {
            const filterTo = new Date(exportFilters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => item.ApprovalDate && new Date(item.ApprovalDate) <= filterTo);
        }
        if (exportFilters.initiativeLevel) {
            result = result.filter(item => (item.InitiativeLevelName || item.InitiativeLevel) === exportFilters.initiativeLevel);
        }
        if (exportFilters.solutionType) {
            result = result.filter(item => (item.SolutionTypeName || item.SolutionType) === exportFilters.solutionType);
        }
        if (exportFilters.userId) {
            result = result.filter(item => {
                if (item.UserId?.toString() === exportFilters.userId.toString()) return true;
                try {
                    const membersList = JSON.parse(item.MembersJSON || '[]');
                    return membersList.some(m => (m.UserId || m.userId || m.Id)?.toString() === exportFilters.userId.toString());
                } catch (e) {
                    return false;
                }
            });
        }

        return result;
    };

    const handlePreviewAdvancedFilters = () => {
        const result = getFilteredExportData();
        setPreviewExportData(result);
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ fromDate: null, toDate: null, initiativeLevel: '', solutionType: '', userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        const result = previewExportData || getFilteredExportData();
        executeExport(result, 'LocNangCao');
        setIsExportModalOpen(false);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header"><div className="header-title"><h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ SÁNG KIẾN</h2></div></div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                {canModify && (
                    <button
                        className="btn-add-new"
                        onClick={() => { setIsReadOnly(false); setIsModalOpen(true); }}
                        style={{ margin: 0, width: isDesktop ? 'auto' : '100%' }}
                    >
                        <i className="fa-solid fa-plus"></i> Thêm mới
                    </button>
                )}

                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p className="sub-title" style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH QUẢN LÝ SÁNG KIẾN</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'none',
                    flexDirection: isDesktop ? 'none' : 'column',
                    gap: '15px'
                }}>
                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <select
                        name="status"
                        className="form-input"
                        style={{ width: '100%' }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">Trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', gridColumn: isDesktop ? 'span 2' : 'none' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" name="keyword" placeholder="Tìm kiếm tên sáng kiến / Quyết định" className="form-input" style={{ width: '100%', paddingLeft: '35px' }} value={filters.keyword} onChange={handleFilterChange} />
                    </div>
                </div>
            </div>

            <InitiativeManagementListing
                data={filteredData}
                filters={filters}
                onEdit={handleEdit}
                onViewDetail={handleViewDetail}
                onDelete={handleRequestDelete}
                onRequestDelete={handleRequestDelete}
                onApproveDelete={handleApproveDelete}
                onCancelDeleteRequest={handleCancelDeleteRequest}
                currentUserEmail={currentUser.Email || ''}
                onApprove={(id) => handleStatusChange(id, 'Đã duyệt')}
                onReject={handleRejectClick}
                onCancelApprove={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onRequestEdit={handleOpenRequestEdit}
                onBulkApprove={(ids) => handleBulkStatusChange(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <InitiativeManagementForm
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setFormData(initialForm); setMembers([]); setEditId(null); setIsReadOnly(false); }}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                readOnly={isReadOnly}
                users={users}
                levels={levels}
                solutionTypes={solutionTypes}
                members={members}
                setMembers={setMembers}
                toast={toast}
                fetchData={fetchData}
            />

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa Sáng kiến</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <strong>Tên sáng kiến: </strong> {selectedInitiativeForEdit?.Name}
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần chỉnh sửa (Sẽ gửi qua Email) *</label>
                                    <textarea className="form-input" rows="6" value={editRequestContent} onChange={(e) => setEditRequestContent(e.target.value)} required></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsEditRequestModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane"></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isRejectModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Từ chối sáng kiến</h3>
                            <button className="close-btn" onClick={() => setIsRejectModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <div style={{ marginBottom: '15px', backgroundColor: '#fff5f5', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #e74c3c' }}>
                                <strong>Lý do từ chối:</strong>
                            </div>
                            <textarea
                                className="form-input"
                                rows="5"
                                value={rejectContent}
                                onChange={(e) => setRejectContent(e.target.value)}
                                placeholder="Nhập lý do từ chối (bắt buộc)..."
                                required
                            />
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="btn-cancel" onClick={() => setIsRejectModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button className="btn-submit" style={{ backgroundColor: '#e74c3c', margin: 0 }} onClick={handleConfirmReject}>Xác nhận từ chối</button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '750px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Xét duyệt)</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Xét duyệt)</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Cấp sáng kiến</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.initiativeLevel} onChange={(e) => setExportFilters({ ...exportFilters, initiativeLevel: e.target.value })}>
                                        <option value="">Toàn bộ</option>
                                        {levels.map(l => <option key={l.Id} value={l.Name}>{l.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại giải pháp</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.solutionType} onChange={(e) => setExportFilters({ ...exportFilters, solutionType: e.target.value })}>
                                        <option value="">Toàn bộ</option>
                                        {solutionTypes.map(st => <option key={st.Id} value={st.Name}>{st.Name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Người dùng (Giảng viên)</label>
                                <SearchableFocusDropdown
                                    value={exportFilters.userId}
                                    options={userOptions}
                                    onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                    placeholder="Chọn giảng viên"
                                    panelUniqueClass="custom-dropdown-panel-initiative-export-advanced-filter"
                                    className="custom-dropdown"
                                    filterBy="label,searchKey"
                                    optionLabel="label"
                                />
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th>Tên sáng kiến</th>
                                                    <th width="150px">Cấp sáng kiến</th>
                                                    <th width="120px" style={{ textAlign: 'center' }}>Ngày duyệt</th>
                                                    <th width="100px" style={{ textAlign: 'center' }}>Trạng thái</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => {
                                                        return (
                                                            <tr key={item.Id}>
                                                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                                <td><div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontWeight: '500', color: '#0056b3' }}>{item.Name}</div></td>
                                                                <td>{item.InitiativeLevelName || item.InitiativeLevel || '---'}</td>
                                                                <td style={{ textAlign: 'center' }}>{item.ApprovalDate ? new Date(item.ApprovalDate).toLocaleDateString('vi-VN') : '---'}</td>
                                                                <td style={{ textAlign: 'center' }}>{item.Status}</td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy sáng kiến nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InitiativeManagement;